import { useBooksStore, type Chapter, type Witness } from '../store/booksStore'
import { useAnchoredRowsStore } from '../store/anchoredRowsStore'
import { dbStorage } from './indexedDBStorage'
import { editorIdentityToContributor, ensureActiveEditorIdentity } from './editorIdentityStore'
import {
  cloneLegacySegmentationResult,
  rehydrateLedgerSafeWitnessStateSnapshotsFromWitnesses,
} from './ledgerSafePersistenceAdapter'
import {
  createLedgerSafeLedgerFromStates,
  hashLedgerSafeValue,
} from './ledgerSafeLedgerStore'
import { buildWorkbenchPath } from './workbenchRoute'
import type { AnchoredRow, UnitMapping } from '../types/anchoredRowTypes'
import type { LedgerSafeEditorialEvent, LedgerSafeEditorialRowSnapshot } from '../types/ledgerSafeEditorialEvents'
import type {
  LedgerSafeLedgerBundle,
  LedgerSafeLedgerSource,
  LedgerSafeLedgerStateRecord,
  LedgerSafeWitnessStateSnapshot,
} from '../types/ledgerSafeLedgers'
import type {
  LedgerSafeContributor,
  LedgerSafeForkHistoryEntry,
  LedgerSafeHumanContributor,
  LedgerSafeLlmContributor,
} from '../types/ledgerSafeProvenance'
import {
  LEDGER_SAFE_SHARED_SNAPSHOT_FORMAT_VERSION,
  LEDGER_SAFE_SHARED_SNAPSHOT_LEGACY_FORMAT_VERSION,
  type LedgerSafeSharedSnapshot,
  type LedgerSafeSharedSnapshotV2,
  type LedgerSafeSharedSnapshotTransportManifest,
  type SharedLedgerRecord,
} from '../types/sharedLedgerSnapshots'
import type {
  ChapterStructuralRangeLayer,
  StructuralRange,
} from '../types/structuralRangeTypes'
import type { WorkbenchContext, WorkbenchProject, WorkbenchWork } from './workbenchDomain'
import type { WorkbenchProjectRecord } from '../types/workbenchProjects'

type IdMap = Record<string, string>

const cloneJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value))

const nowIso = (): string => new Date().toISOString()

const generateLocalId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const compactLabel = (value: string | undefined, fallback: string): string => {
  const trimmed = (value || '').trim()
  return trimmed || fallback
}

const GENERATED_FORK_SUFFIX_PATTERN = /\s*\((?:shared fork|forked copy|fork copy)\)\s*$/i

export const stripGeneratedForkSuffixes = (value: string, fallback = 'Untitled ledger'): string => {
  let trimmed = (value || '').trim()
  while (GENERATED_FORK_SUFFIX_PATTERN.test(trimmed)) {
    trimmed = trimmed.replace(GENERATED_FORK_SUFFIX_PATTERN, '').trim()
  }
  return trimmed || fallback
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
)

const asString = (value: unknown): string | undefined => {
  const trimmed = String(value || '').trim()
  return trimmed || undefined
}

const asNumber = (value: unknown): number | undefined => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}

const inferProviderFromModelId = (modelId: string): string | undefined => {
  const normalized = modelId.toLowerCase()
  if (normalized.includes('gemini')) {
    return 'google'
  }
  if (normalized.includes('gpt') || normalized.includes('o3') || normalized.includes('o4')) {
    return 'openai'
  }
  if (normalized.includes('claude')) {
    return 'anthropic'
  }
  return undefined
}

const normalizeContributorKey = (contributor: LedgerSafeContributor): string => (
  contributor.kind === 'llm'
    ? `llm:${contributor.id}:${contributor.role || ''}:${contributor.promptId || ''}:${contributor.promptVersion || ''}`
    : `human:${contributor.id}:${contributor.role || ''}`
)

const mergeContributors = (...groups: Array<Array<LedgerSafeContributor | null | undefined> | undefined>): LedgerSafeContributor[] => {
  const contributorsByKey = new Map<string, LedgerSafeContributor>()
  groups.flat().forEach(contributor => {
    if (!contributor) {
      return
    }
    contributorsByKey.set(normalizeContributorKey(contributor), contributor)
  })
  return Array.from(contributorsByKey.values())
}

const getHumanContributorInitials = (contributor: LedgerSafeHumanContributor): string => {
  const label = (contributor.displayName || contributor.email || contributor.id || '').trim()
  const tokens = label.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').match(/[\p{L}\p{N}]+/gu) || []
  const chars = tokens.length >= 2
    ? tokens.slice(0, 3).map(token => token[0])
    : (tokens[0] || label || '?').slice(0, 2).split('')
  return chars.join('').toLocaleUpperCase()
}

const pushRecentInitial = (initials: string[], contributor: LedgerSafeHumanContributor | undefined | null): void => {
  if (!contributor) {
    return
  }
  const initial = getHumanContributorInitials(contributor)
  if (!initial) {
    return
  }
  const existingIndex = initials.indexOf(initial)
  if (existingIndex >= 0) {
    initials.splice(existingIndex, 1)
  }
  initials.push(initial)
}

const formatForkDisplayDate = (isoDate: string): string => {
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) {
    return isoDate.slice(0, 10)
  }
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const buildLedgerForkDisplayMetadata = ({
  contributors = [],
  forkHistory,
}: {
  contributors?: LedgerSafeContributor[]
  forkHistory: LedgerSafeForkHistoryEntry[]
}): {
  forkGeneration: number
  forkEditorInitials: string[]
  forkedAt: string
  forkDisplayLabel: string
} => {
  const initials: string[] = []
  contributors.forEach(contributor => {
    if (contributor.kind === 'human') {
      pushRecentInitial(initials, contributor)
    }
  })
  forkHistory.forEach(entry => pushRecentInitial(initials, entry.forkedBy))

  const forkGeneration = Math.max(1, forkHistory.length)
  const forkedAt = forkHistory[forkHistory.length - 1]?.forkedAt || nowIso()
  const editorLabel = initials.length ? initials.join(', ') : 'Unknown editor'
  return {
    forkGeneration,
    forkEditorInitials: initials,
    forkedAt,
    forkDisplayLabel: `Fork ${forkGeneration} · ${editorLabel} · ${formatForkDisplayDate(forkedAt)}`,
  }
}

export const buildLlmContributorFromLedgerSource = (
  source: LedgerSafeLedgerSource,
): LedgerSafeLlmContributor | null => {
  const diagnostics = asRecord(source.diagnostics) || {}
  const summary = asRecord(source.summary) || {}
  const generationConfig = asRecord(diagnostics.generation_config)
    || asRecord(summary.generation_config)
    || asRecord(diagnostics.generationConfig)
    || asRecord(summary.generationConfig)
    || null
  const thinkingConfig = asRecord(generationConfig?.thinkingConfig)
  const modelId = asString(diagnostics.model_id)
    || asString(diagnostics.model)
    || asString(summary.model_id)
    || asString(summary.model)
  if (!modelId) {
    return null
  }
  const provider = asString(diagnostics.provider)
    || asString(summary.provider)
    || inferProviderFromModelId(modelId)
  const promptId = asString(diagnostics.prompt_id) || asString(summary.prompt_id)
  const promptVersion = asString(diagnostics.prompt_version) || asString(summary.prompt_version)
  const temperature = asNumber(generationConfig?.temperature)
    ?? asNumber(diagnostics.temperature)
    ?? asNumber(summary.temperature)
  const maxOutputTokens = asNumber(generationConfig?.maxOutputTokens)
    ?? asNumber(generationConfig?.max_output_tokens)
    ?? asNumber(diagnostics.max_output_tokens)
    ?? asNumber(summary.max_output_tokens)
  const topP = asNumber(generationConfig?.topP) ?? asNumber(generationConfig?.top_p)
  const topK = asNumber(generationConfig?.topK) ?? asNumber(generationConfig?.top_k)
  const thinkingLevel = asString(thinkingConfig?.thinkingLevel)
    || asString(diagnostics.thinking_level)
    || asString(summary.thinking_level)
  return {
    kind: 'llm',
    id: `llm:${provider || 'unknown'}:${modelId}:${promptId || 'ledger-draft'}`,
    displayName: provider ? `${provider} ${modelId}` : modelId,
    role: 'initial_drafter',
    ...(provider ? { provider } : {}),
    modelId,
    ...(temperature !== undefined ? { temperature } : {}),
    ...(topP !== undefined ? { topP } : {}),
    ...(topK !== undefined ? { topK } : {}),
    ...(maxOutputTokens !== undefined ? { maxOutputTokens } : {}),
    ...(thinkingLevel ? { thinkingLevel } : {}),
    ...(promptId ? { promptId } : {}),
    ...(promptVersion ? { promptVersion } : {}),
    ...(generationConfig ? { generationConfig } : {}),
    ...(source.artifactCreatedAt || source.runCreatedAt ? { contributedAt: source.artifactCreatedAt || source.runCreatedAt } : {}),
    source: source.type,
  }
}

const getLedgerSourceContributors = (source: LedgerSafeLedgerSource): LedgerSafeContributor[] => (
  mergeContributors(
    source.contributors,
    [buildLlmContributorFromLedgerSource(source)],
  )
)

const getForkHistoryFromSource = (source: LedgerSafeLedgerSource): LedgerSafeForkHistoryEntry[] => {
  if (Array.isArray(source.forkHistory)) {
    return cloneJson(source.forkHistory) as LedgerSafeForkHistoryEntry[]
  }
  const diagnostics = asRecord(source.diagnostics)
  const forkHistory = diagnostics?.fork_history || diagnostics?.forkHistory
  return Array.isArray(forkHistory)
    ? cloneJson(forkHistory) as LedgerSafeForkHistoryEntry[]
    : []
}

const stripWitnessStateSegmentationForTransport = (
  state: LedgerSafeLedgerStateRecord,
): { state: LedgerSafeLedgerStateRecord; strippedSnapshotCount: number } => {
  let strippedSnapshotCount = 0
  const compactState: LedgerSafeLedgerStateRecord = {
    ...cloneJson(state),
    witnessStates: (state.witnessStates || []).map(witnessState => {
      const { segmentationResult: _segmentationResult, ...compactWitnessState } = cloneJson(witnessState)
      if (witnessState.segmentationResult !== undefined) {
        strippedSnapshotCount += 1
      }
      return compactWitnessState
    }),
  }
  return { state: compactState, strippedSnapshotCount }
}

const buildEditorialEventTransportManifest = (
  editorialEvents: LedgerSafeEditorialEvent[],
): LedgerSafeSharedSnapshotTransportManifest['editorialEvents'] => {
  const operationCounts = editorialEvents.reduce<Record<string, number>>((counts, event) => {
    const kind = event.operation?.kind || 'unknown'
    counts[kind] = (counts[kind] || 0) + 1
    return counts
  }, {})
  return {
    policy: 'manifest-only',
    originalCount: editorialEvents.length,
    exportedCount: 0,
    omittedFields: ['beforeRows', 'afterRows', 'operation', 'toolCallProjection', 'uiContext'],
    operationCounts,
    contributorsPreservedInProvenance: true,
  }
}

const buildSharedSnapshotTransport = ({
  baseline,
  working,
  editorialEvents,
}: {
  baseline: LedgerSafeLedgerStateRecord
  working?: LedgerSafeLedgerStateRecord | null
  editorialEvents: LedgerSafeEditorialEvent[]
}): {
  baseline: LedgerSafeLedgerStateRecord
  working: LedgerSafeLedgerStateRecord | null
  transport: LedgerSafeSharedSnapshotTransportManifest
} => {
  const compactBaseline = stripWitnessStateSegmentationForTransport(baseline)
  const compactWorking = working ? stripWitnessStateSegmentationForTransport(working) : null
  return {
    baseline: compactBaseline.state,
    working: compactWorking?.state || null,
    transport: {
      profile: 'compact-web-share.v1',
      stateWitnessSegmentation: {
        policy: 'rehydrate-from-witnesses',
        strippedSnapshotCount: compactBaseline.strippedSnapshotCount + (compactWorking?.strippedSnapshotCount || 0),
      },
      editorialEvents: buildEditorialEventTransportManifest(editorialEvents),
    },
  }
}

const filterLedgerRowsByWitnessIds = (rows: AnchoredRow[], includedWitnessIds: Set<string>): AnchoredRow[] => (
  cloneJson(rows || []).map(row => ({
    ...row,
    unitMappings: (row.unitMappings || []).filter(mapping => includedWitnessIds.has(mapping.witnessId)),
  }))
)

const filterLedgerWitnessStatesByWitnessIds = (
  states: LedgerSafeWitnessStateSnapshot[],
  includedWitnessIds: Set<string>,
): LedgerSafeWitnessStateSnapshot[] => (
  cloneJson(states || []).filter(state => includedWitnessIds.has(state.witnessId))
)

const filterLedgerStateByWitnessIds = (
  state: LedgerSafeLedgerStateRecord,
  includedWitnessIds: Set<string>,
): LedgerSafeLedgerStateRecord => {
  const rows = filterLedgerRowsByWitnessIds(state.rows || [], includedWitnessIds)
  return {
    ...cloneJson(state),
    rows,
    rowCount: rows.length,
    witnessStates: filterLedgerWitnessStatesByWitnessIds(state.witnessStates || [], includedWitnessIds),
  }
}

const filterSiglaList = (values: string[] | undefined, includedSigla: Set<string>): string[] => (
  (values || []).filter(sigla => includedSigla.has(sigla))
)

const filterLedgerSourceByWitnessSigla = (
  source: LedgerSafeLedgerSource,
  includedSigla: Set<string>,
): LedgerSafeLedgerSource => {
  const cloned = cloneJson(source)
  return {
    ...cloned,
    representativeWitnessSigla: filterSiglaList(cloned.representativeWitnessSigla, includedSigla),
    selectedWitnessSigla: filterSiglaList(cloned.selectedWitnessSigla, includedSigla),
  }
}

const filterLedgerBundleByWitnessIds = (
  bundle: LedgerSafeLedgerBundle,
  includedWitnessIds: Set<string>,
  includedWitnesses: Witness[],
): LedgerSafeLedgerBundle => {
  const includedSigla = new Set(includedWitnesses.map(witness => witness.sigla))
  const filteredStates = bundle.states.map(state => filterLedgerStateByWitnessIds(state, includedWitnessIds))
  const filteredBaseline = filterLedgerStateByWitnessIds(bundle.baseline, includedWitnessIds)
  const filteredWorking = bundle.latestWorking
    ? filterLedgerStateByWitnessIds(bundle.latestWorking, includedWitnessIds)
    : null

  return {
    ledger: {
      ...cloneJson(bundle.ledger),
      source: filterLedgerSourceByWitnessSigla(bundle.ledger.source, includedSigla),
    },
    states: filteredStates,
    baseline: filteredBaseline,
    latestWorking: filteredWorking,
  }
}

const filterStructuralRangeLayerByRows = (
  layer: ChapterStructuralRangeLayer | null | undefined,
  rows: AnchoredRow[],
): ChapterStructuralRangeLayer | null => {
  if (!layer) return null
  const includedRowIds = new Set(rows.map(row => row.id))
  const includedRanges = layer.ranges.filter(range => (
    includedRowIds.has(range.startRowId) && includedRowIds.has(range.endRowId)
  ))
  const includedRangeIds = new Set(includedRanges.map(range => range.id))
  return {
    ...cloneJson(layer),
    ranges: includedRanges.map(range => ({
      ...cloneJson(range),
      parentRangeId: range.parentRangeId && includedRangeIds.has(range.parentRangeId)
        ? range.parentRangeId
        : null,
    })),
  }
}

export const buildLedgerSafeSharedSnapshot = ({
  project,
  work,
  context,
  ledgerBundle,
  activeState,
  witnesses,
  editorialEvents,
  includedWitnessIds,
  appVersion,
  releaseName,
  forkedFrom,
  sharedBy,
  structuralLayer,
}: {
  project: WorkbenchProject
  work: WorkbenchWork
  context: WorkbenchContext
  ledgerBundle: LedgerSafeLedgerBundle
  activeState: LedgerSafeLedgerStateRecord
  witnesses: Witness[]
  editorialEvents: LedgerSafeEditorialEvent[]
  includedWitnessIds?: string[]
  appVersion?: string
  releaseName?: string
  forkedFrom?: LedgerSafeSharedSnapshot['provenance']['forked_from']
  sharedBy?: LedgerSafeHumanContributor
  structuralLayer?: ChapterStructuralRangeLayer | null
}): LedgerSafeSharedSnapshotV2 => {
  const sharedAt = nowIso()
  const includedWitnessIdSet = includedWitnessIds?.length ? new Set(includedWitnessIds) : null
  const scopedWitnesses = includedWitnessIdSet
    ? witnesses.filter(witness => includedWitnessIdSet.has(witness.id))
    : witnesses
  const scopedLedgerBundle = includedWitnessIdSet
    ? filterLedgerBundleByWitnessIds(ledgerBundle, includedWitnessIdSet, scopedWitnesses)
    : ledgerBundle
  const scopedActiveState = includedWitnessIdSet
    ? filterLedgerStateByWitnessIds(activeState, includedWitnessIdSet)
    : activeState
  const activeRows = scopedActiveState.rows || []
  const ledgerSourceContributors = getLedgerSourceContributors(scopedLedgerBundle.ledger.source)
  const eventContributors = editorialEvents.map(event => event.actor)
  const transportSnapshot = buildSharedSnapshotTransport({
    baseline: scopedLedgerBundle.baseline,
    working: scopedLedgerBundle.latestWorking,
    editorialEvents,
  })
  const contributors = mergeContributors(
    ledgerSourceContributors,
    eventContributors,
    sharedBy ? [{ ...sharedBy, role: 'sharer', contributedAt: sharedAt }] : [],
  )
  const forkHistory = getForkHistoryFromSource(scopedLedgerBundle.ledger.source)
  return {
    format_version: LEDGER_SAFE_SHARED_SNAPSHOT_FORMAT_VERSION,
    snapshot_schema_version: 2,
    created_at: sharedAt,
    source_app: {
      name: 'segmentation-agent',
      ...(appVersion ? { version: appVersion } : {}),
      ...(releaseName ? { release_name: releaseName } : {}),
    },
    project: {
      id: project.id,
      name: project.name,
      description: project.description,
    },
    work: {
      id: work.id,
      name: work.name,
      description: work.description,
    },
    context: {
      id: context.id,
      name: context.name,
      description: context.description,
      label_prefix: context.label_prefix,
    },
    ledger: cloneJson(scopedLedgerBundle.ledger),
    states: {
      active_kind: scopedActiveState.kind,
      active_state_id: scopedActiveState.id,
      baseline: transportSnapshot.baseline,
      working: transportSnapshot.working,
    },
    witnesses: cloneJson(scopedWitnesses),
    editorial_events: [],
    structural_layer: filterStructuralRangeLayerByRows(structuralLayer, activeRows),
    transport: transportSnapshot.transport,
    permissions: {
      visibility: 'link',
      defaultAccess: 'read-only',
      allowedActions: ['view', 'import_as_new_ledger', 'recover_editable_state', 'reshare_fork'],
      enforcement: 'metadata-only',
    },
    provenance: {
      origin: 'local-ledger-safe',
      source_project_id: project.id,
      source_work_id: work.id,
      source_context_id: context.id,
      source_ledger_id: scopedLedgerBundle.ledger.id,
      source_active_state_id: scopedActiveState.id,
      ...(sharedBy ? { created_by: sharedBy, shared_by: { ...sharedBy, role: 'sharer', contributedAt: sharedAt } } : {}),
      contributors,
      fork_history: forkHistory,
      ...(forkedFrom ? { forked_from: forkedFrom } : {}),
    },
    summary: {
      title: `${compactLabel(work.name, 'Shared work')} / ${compactLabel(context.name, 'Shared context')}`,
      project_name: compactLabel(project.name, 'Shared project'),
      work_name: compactLabel(work.name, 'Shared work'),
      context_name: compactLabel(context.name, 'Shared context'),
      ledger_name: compactLabel(scopedLedgerBundle.ledger.name, 'Shared collation'),
      witness_count: scopedWitnesses.length,
      row_count: activeRows.length,
      editorial_event_count: editorialEvents.length,
      active_state_kind: scopedActiveState.kind,
      shared_at: sharedAt,
    },
  }
}

const buildWitnessIdMap = (witnesses: Witness[], generatedIds: string[]): IdMap => (
  witnesses.reduce<IdMap>((accumulator, witness, index) => {
    accumulator[witness.id] = generatedIds[index]
    return accumulator
  }, {})
)

export const buildSharedLedgerRowIdMap = (rows: AnchoredRow[], importToken: string): IdMap => (
  rows.reduce<IdMap>((accumulator, row) => {
    accumulator[row.id] = `${row.id}::fork::${importToken}`
    return accumulator
  }, {})
)

const rebaseScalar = (value: unknown, rowIdMap: IdMap, witnessIdMap: IdMap): unknown => {
  if (typeof value !== 'string') {
    return value
  }
  return rowIdMap[value] || witnessIdMap[value] || value
}

const rebaseUnknownValue = (value: unknown, rowIdMap: IdMap, witnessIdMap: IdMap): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => rebaseUnknownValue(item, rowIdMap, witnessIdMap))
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((accumulator, [key, nested]) => {
      accumulator[key] = rebaseUnknownValue(nested, rowIdMap, witnessIdMap)
      return accumulator
    }, {})
  }
  return rebaseScalar(value, rowIdMap, witnessIdMap)
}

const rebaseUnitMappings = (
  unitMappings: UnitMapping[],
  witnessIdMap: IdMap,
  rowIdMap: IdMap = {},
): UnitMapping[] => (
  unitMappings.map(mapping => ({
    ...mapping,
    witnessId: witnessIdMap[mapping.witnessId] || mapping.witnessId,
    ...(mapping.transpositionOriginRowId
      ? { transpositionOriginRowId: rowIdMap[mapping.transpositionOriginRowId] || mapping.transpositionOriginRowId }
      : {}),
  }))
)

export const rebaseSharedLedgerRows = ({
  rows,
  contextId,
  witnessIdMap,
  rowIdMap,
}: {
  rows: AnchoredRow[]
  contextId: string
  witnessIdMap: IdMap
  rowIdMap: IdMap
}): AnchoredRow[] => (
  cloneJson(rows).map(row => ({
    ...row,
    id: rowIdMap[row.id] || row.id,
    chapterId: contextId,
    unitMappings: rebaseUnitMappings(row.unitMappings || [], witnessIdMap, rowIdMap),
  }))
)

export const rebaseSharedLedgerWitnessStates = (
  states: LedgerSafeWitnessStateSnapshot[],
  witnessIdMap: IdMap,
): LedgerSafeWitnessStateSnapshot[] => (
  cloneJson(states || []).map(state => ({
    ...state,
    witnessId: witnessIdMap[state.witnessId] || state.witnessId,
  }))
)

export const rehydrateSharedLedgerWitnessStatesFromSnapshot = (
  snapshot: LedgerSafeSharedSnapshot,
  states: LedgerSafeWitnessStateSnapshot[],
): LedgerSafeWitnessStateSnapshot[] => rehydrateLedgerSafeWitnessStateSnapshotsFromWitnesses(states || [], snapshot.witnesses)

const rebaseEditorialRowSnapshot = (
  row: LedgerSafeEditorialRowSnapshot,
  contextId: string,
  rowIdMap: IdMap,
  witnessIdMap: IdMap,
): LedgerSafeEditorialRowSnapshot => ({
  ...row,
  id: rowIdMap[row.id] || row.id,
  chapterId: contextId,
  unitMappings: rebaseUnitMappings(row.unitMappings || [], witnessIdMap, rowIdMap),
})

export const rebaseSharedLedgerEditorialEvents = ({
  events,
  contextId,
  witnessIdMap,
  rowIdMap,
  importToken,
}: {
  events: LedgerSafeEditorialEvent[]
  contextId: string
  witnessIdMap: IdMap
  rowIdMap: IdMap
  importToken: string
}): LedgerSafeEditorialEvent[] => (
  cloneJson(events || []).map(event => ({
    ...event,
    id: `${event.id}::fork::${importToken}`,
    chapterId: contextId,
    affectedWitnessIds: event.affectedWitnessIds.map(witnessId => witnessIdMap[witnessId] || witnessId),
    affectedRowIds: event.affectedRowIds.map(rowId => rowIdMap[rowId] || rowId),
    beforeRows: event.beforeRows.map(row => rebaseEditorialRowSnapshot(row, contextId, rowIdMap, witnessIdMap)),
    afterRows: event.afterRows.map(row => rebaseEditorialRowSnapshot(row, contextId, rowIdMap, witnessIdMap)),
    operation: {
      ...event.operation,
      params: rebaseUnknownValue(event.operation.params, rowIdMap, witnessIdMap) as Record<string, unknown>,
    },
    toolCallProjection: event.toolCallProjection
      ? rebaseUnknownValue(event.toolCallProjection, rowIdMap, witnessIdMap) as LedgerSafeEditorialEvent['toolCallProjection']
      : null,
    uiContext: {
      ...event.uiContext,
      scopeWitnessIds: event.uiContext.scopeWitnessIds.map(witnessId => witnessIdMap[witnessId] || witnessId),
      selectedWitnessId: event.uiContext.selectedWitnessId
        ? witnessIdMap[event.uiContext.selectedWitnessId] || event.uiContext.selectedWitnessId
        : null,
      selectedRowIds: event.uiContext.selectedRowIds.map(rowId => rowIdMap[rowId] || rowId),
      diffPivotWitnessId: event.uiContext.diffPivotWitnessId
        ? witnessIdMap[event.uiContext.diffPivotWitnessId] || event.uiContext.diffPivotWitnessId
        : null,
    },
    metadata: {
      ...(event.metadata || {}),
      forked_from_event_id: event.id,
    },
  }))
)

export const rebaseSharedLedgerStructuralLayer = ({
  layer,
  contextId,
  bookId,
  rowIdMap,
  importToken,
  availableRows,
}: {
  layer: ChapterStructuralRangeLayer
  contextId: string
  bookId: string
  rowIdMap: IdMap
  importToken: string
  availableRows: AnchoredRow[]
}): ChapterStructuralRangeLayer => {
  const availableRebasedRowIds = new Set(availableRows.map(row => row.id))
  const sourceRanges = layer.ranges.filter(range => (
    availableRebasedRowIds.has(rowIdMap[range.startRowId] || range.startRowId)
    && availableRebasedRowIds.has(rowIdMap[range.endRowId] || range.endRowId)
  ))
  const rangeIdMap = new Map(
    sourceRanges.map(range => [range.id, `${range.id}::fork::${importToken}`]),
  )
  const rebaseRange = (range: StructuralRange): StructuralRange => ({
    ...cloneJson(range),
    id: rangeIdMap.get(range.id) || `${range.id}::fork::${importToken}`,
    startRowId: rowIdMap[range.startRowId] || range.startRowId,
    endRowId: rowIdMap[range.endRowId] || range.endRowId,
    parentRangeId: range.parentRangeId
      ? rangeIdMap.get(range.parentRangeId) || null
      : null,
  })
  return {
    ...cloneJson(layer),
    id: contextId,
    chapterId: contextId,
    bookId,
    version: 1,
    ranges: sourceRanges.map(rebaseRange),
    createdAt: nowIso(),
    updatedAt: nowIso(),
  }
}

const getAllSnapshotRows = (snapshot: LedgerSafeSharedSnapshot): AnchoredRow[] => {
  const rowsById = new Map<string, AnchoredRow>()
  snapshot.states.baseline.rows.forEach(row => rowsById.set(row.id, row))
  if (snapshot.states.working?.rows) {
    snapshot.states.working.rows.forEach(row => rowsById.set(row.id, row))
  }
  return Array.from(rowsById.values())
}

const buildSharedLedgerSource = (
  snapshot: LedgerSafeSharedSnapshot,
  record: SharedLedgerRecord | null,
  forkEntry: LedgerSafeForkHistoryEntry,
): LedgerSafeLedgerSource => {
  const forkHistory = [
    ...(snapshot.provenance.fork_history || []),
    forkEntry,
  ]
  const contributors = mergeContributors(
    snapshot.provenance.contributors,
    [forkEntry.forkedBy],
  )
  const forkDisplay = buildLedgerForkDisplayMetadata({
    contributors,
    forkHistory,
  })

  return {
    type: 'shared-snapshot',
    representativeWitnessSigla: snapshot.ledger.source.representativeWitnessSigla || [],
    selectedWitnessSigla: snapshot.ledger.source.selectedWitnessSigla || snapshot.witnesses.map(witness => witness.sigla),
    artifactHash: record?.snapshot_hash || hashLedgerSafeValue(snapshot),
    artifactCreatedAt: record?.created_at || snapshot.created_at,
    summary: {
      ...(snapshot.ledger.source.summary || {}),
      shared_snapshot: true,
      shared_snapshot_summary: cloneJson(snapshot.summary),
      ...(record ? {
        shared_snapshot_record: {
          share_id: record.share_id,
          snapshot_hash: record.snapshot_hash,
          created_at: record.created_at,
          snapshot_format_version: record.snapshot_format_version,
        },
      } : {}),
      original_project: snapshot.project,
      original_work: snapshot.work,
      original_context: snapshot.context,
      original_ledger_id: snapshot.ledger.id,
      original_ledger_name: snapshot.ledger.name,
      share_id: record?.share_id,
      fork_generation: forkDisplay.forkGeneration,
      fork_editor_initials: forkDisplay.forkEditorInitials,
      forked_at: forkDisplay.forkedAt,
      fork_display_label: forkDisplay.forkDisplayLabel,
    },
    diagnostics: {
      ...(snapshot.ledger.source.diagnostics || {}),
      forked_from: snapshot.provenance,
      fork_history: forkHistory,
    },
    contributors,
    forkHistory,
  }
}

const witnessInputFromSnapshot = (witness: Witness): Parameters<ReturnType<typeof useBooksStore.getState>['createWitness']>[1] => {
  const {
    id: _id,
    chapter_id: _chapterId,
    book_id: _bookId,
    created_at: _createdAt,
    processing_status: _processingStatus,
    source_object_id: _sourceObjectId,
    ...input
  } = cloneJson(witness)
  return input
}

export interface ImportSharedLedgerSnapshotResult {
  projectId: string
  workId: string
  contextId: string
  ledgerId: string
  activeStateKind: 'baseline' | 'working'
  workbenchPath: string
  witnessIdMap: IdMap
  rowIdMap: IdMap
}

export const importSharedLedgerSnapshotAsFork = async ({
  snapshot,
  record = null,
  mode,
}: {
  snapshot: LedgerSafeSharedSnapshot
  record?: SharedLedgerRecord | null
  mode: 'baseline' | 'working'
}): Promise<ImportSharedLedgerSnapshotResult> => {
  if (
    snapshot.format_version !== LEDGER_SAFE_SHARED_SNAPSHOT_FORMAT_VERSION
    && snapshot.format_version !== LEDGER_SAFE_SHARED_SNAPSHOT_LEGACY_FORMAT_VERSION
  ) {
    throw new Error('Unsupported shared collation snapshot format.')
  }
  const booksStore = useBooksStore.getState()
  const rowsStore = useAnchoredRowsStore.getState()
  const importToken = generateLocalId('shared-import').replace(/[^a-zA-Z0-9_-]/g, '-')
  const projectId = `shared-ledger-${importToken}`
  const importedAt = nowIso()
  const editorIdentity = await ensureActiveEditorIdentity()
  const forkedBy = editorIdentityToContributor(editorIdentity, 'forker', importedAt)
  const forkEntry: LedgerSafeForkHistoryEntry = {
    shareId: record?.share_id,
    snapshotHash: record?.snapshot_hash || hashLedgerSafeValue(snapshot),
    sourceLedgerId: snapshot.provenance.source_ledger_id || snapshot.ledger.id,
    sourceProjectId: snapshot.provenance.source_project_id,
    sourceWorkId: snapshot.provenance.source_work_id,
    sourceContextId: snapshot.provenance.source_context_id,
    forkedAt: importedAt,
    forkedBy,
    mode,
  }

  const workId = await booksStore.createBook(
    stripGeneratedForkSuffixes(compactLabel(snapshot.work.name, 'Shared work'), 'Shared work'),
    snapshot.work.description || `Forked from shared collation ${record?.share_id || snapshot.ledger.id}.`,
  )
  const contextId = await booksStore.createChapter(
    workId,
    compactLabel(snapshot.context.name, 'Shared context'),
    snapshot.context.description,
  )
  const createdChapter = booksStore.getChapter(contextId)
  if (createdChapter && snapshot.context.label_prefix) {
    const updatedChapter: Chapter = {
      ...createdChapter,
      label_prefix: snapshot.context.label_prefix,
    }
    await dbStorage.put('chapters', updatedChapter)
    useBooksStore.setState(state => ({
      chapters: state.chapters.map(chapter => chapter.id === contextId ? updatedChapter : chapter),
    }))
  }

  const generatedWitnessIds: string[] = []
  for (const witness of snapshot.witnesses) {
    const witnessId = await booksStore.createWitness(contextId, witnessInputFromSnapshot(witness))
    generatedWitnessIds.push(witnessId)
    await booksStore.updateWitness(witnessId, {
      preprocessing_result: witness.preprocessing_result,
      segmentation_result: cloneLegacySegmentationResult(witness.segmentation_result),
      segmentation_runs: witness.segmentation_runs,
      active_segmentation_run: witness.active_segmentation_run,
      processing_status: witness.processing_status,
      language: witness.language,
      witness_type: witness.witness_type,
      transcription_type: witness.transcription_type,
      coverage_status: witness.coverage_status,
      lacunose_status: witness.lacunose_status,
    })
  }

  const witnessIdMap = buildWitnessIdMap(snapshot.witnesses, generatedWitnessIds)
  const rowIdMap = buildSharedLedgerRowIdMap(getAllSnapshotRows(snapshot), importToken)
  const baselineRows = rebaseSharedLedgerRows({
    rows: snapshot.states.baseline.rows,
    contextId,
    witnessIdMap,
    rowIdMap,
  })
  const workingRows = snapshot.states.working?.rows
    ? rebaseSharedLedgerRows({
        rows: snapshot.states.working.rows,
        contextId,
        witnessIdMap,
        rowIdMap,
      })
    : null
  const baselineWitnessStates = rebaseSharedLedgerWitnessStates(
    rehydrateSharedLedgerWitnessStatesFromSnapshot(snapshot, snapshot.states.baseline.witnessStates),
    witnessIdMap,
  )
  const workingWitnessStates = snapshot.states.working?.witnessStates
    ? rebaseSharedLedgerWitnessStates(
        rehydrateSharedLedgerWitnessStatesFromSnapshot(snapshot, snapshot.states.working.witnessStates),
        witnessIdMap,
      )
    : null
  const shouldRecoverWorking = mode === 'working' && Boolean(workingRows)
  const activeRows = shouldRecoverWorking && workingRows ? workingRows : baselineRows

  await rowsStore.upsertRows(contextId, activeRows, { autoRelabel: false })
  if ('structural_layer' in snapshot && snapshot.structural_layer) {
    const rebasedStructuralLayer = rebaseSharedLedgerStructuralLayer({
      layer: snapshot.structural_layer,
      contextId,
      bookId: workId,
      rowIdMap,
      importToken,
      availableRows: activeRows,
    })
    await dbStorage.put('chapter_structural_layers', rebasedStructuralLayer)
  }
  const ledgerBundle = await createLedgerSafeLedgerFromStates({
    contextId,
    name: stripGeneratedForkSuffixes(compactLabel(snapshot.ledger.name, 'Shared collation'), 'Shared collation'),
    source: buildSharedLedgerSource(snapshot, record, forkEntry),
    baselineRows,
    baselineWitnessStates,
    workingRows,
    workingWitnessStates,
    workingLabel: snapshot.states.working?.label || 'Recovered shared edited state',
    activateStateKind: shouldRecoverWorking ? 'working' : 'baseline',
  })

  const rebasedEvents = rebaseSharedLedgerEditorialEvents({
    events: snapshot.editorial_events || [],
    contextId,
    witnessIdMap,
    rowIdMap,
    importToken,
  })
  for (const event of rebasedEvents) {
    await dbStorage.put('ledger_safe_edit_events', event)
  }

  const timestamp = Date.now()
  const project: WorkbenchProjectRecord = {
    id: projectId,
    name: stripGeneratedForkSuffixes(compactLabel(snapshot.project.name, 'Shared project'), 'Shared project'),
    description: `Imported from shared collation ${record?.share_id || snapshot.ledger.id}.`,
    source_book_ids: [workId],
    build_targets: [],
    created_at: timestamp,
    last_modified: timestamp,
  }
  await dbStorage.saveWorkbenchProject(project)

  booksStore.setCurrentBook(workId)
  booksStore.setCurrentChapter(contextId)

  return {
    projectId,
    workId,
    contextId,
    ledgerId: ledgerBundle.ledger.id,
    activeStateKind: shouldRecoverWorking ? 'working' : 'baseline',
    workbenchPath: buildWorkbenchPath({ projectId, workId, contextId }),
    witnessIdMap,
    rowIdMap,
  }
}
